import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { verifyMagicLink, setPassword, resendWelcome } from '../services/authService';

const STORAGE_KEY = 'minversity-user';

// ---------------------------------------------------------------------------
// States the page can be in
// ---------------------------------------------------------------------------
const STATE = {
  VERIFYING: 'verifying',   // initial — validating the token
  FORM: 'form',             // token valid, show set-password form
  SUCCESS: 'success',       // password set, user logged in
  INVALID: 'invalid',       // token not found
  EXPIRED: 'expired',       // token found but expired
  USED: 'used',             // token already consumed
};

function Shell({ children }) {
  return (
    <div className="auth-shell">
      <div className="backdrop" aria-hidden="true" />
      <main className="auth-wrapper">
        <div className="auth-card" role="main">
          <div className="brand-pill">
            <span className="brand-icon">M</span>
            <span className="brand-name">Minversity</span>
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}

export default function SetPasswordPage({ onActivated }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [pageState, setPageState] = useState(STATE.VERIFYING);
  const [userInfo, setUserInfo] = useState(null); // { firstName, email, role }
  const [password, setPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState(''); // '' | 'sending' | 'sent' | 'error'

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setPageState(STATE.INVALID);
      return;
    }

    verifyMagicLink(token)
      .then((data) => {
        setUserInfo({ firstName: data.firstName, email: data.email, role: data.role });
        setPageState(STATE.FORM);
      })
      .catch((err) => {
        const code = err.code || 'INVALID';
        if (code === 'EXPIRED') setPageState(STATE.EXPIRED);
        else if (code === 'USED') setPageState(STATE.USED);
        else setPageState(STATE.INVALID);
      });
  }, [token]);

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setFormError('');

    if (password.length < 8) {
      setFormError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setFormError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const { user } = await setPassword(token, password);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
      setPageState(STATE.SUCCESS);
      // Give the success screen a moment, then navigate to dashboard
      setTimeout(() => {
        onActivated(user);
        navigate('/dashboard');
      }, 1800);
    } catch (err) {
      const code = err.code || '';
      if (code === 'EXPIRED') {
        setPageState(STATE.EXPIRED);
      } else if (code === 'USED') {
        setPageState(STATE.USED);
      } else {
        setFormError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResendStatus('sending');
    try {
      await resendWelcome(resendEmail.trim().toLowerCase());
      setResendStatus('sent');
    } catch (err) {
      setResendStatus('error');
    }
  };

  // ---------------------------------------------------------------------------
  // Verifying
  // ---------------------------------------------------------------------------
  if (pageState === STATE.VERIFYING) {
    return (
      <Shell>
        <p className="subtitle" style={{ marginTop: '1.5rem' }}>Verifying your link…</p>
      </Shell>
    );
  }

  // ---------------------------------------------------------------------------
  // Success
  // ---------------------------------------------------------------------------
  if (pageState === STATE.SUCCESS) {
    return (
      <Shell>
        <h1 style={{ marginTop: '1rem' }}>You're all set!</h1>
        <p className="subtitle">Taking you to your dashboard…</p>
      </Shell>
    );
  }

  // ---------------------------------------------------------------------------
  // Already used
  // ---------------------------------------------------------------------------
  if (pageState === STATE.USED) {
    return (
      <Shell>
        <h1 style={{ marginTop: '1rem' }}>Link already used</h1>
        <p className="subtitle">Your account is already activated. Sign in below.</p>
        <button className="primary-btn" style={{ marginTop: '1.5rem', width: '100%' }}
          onClick={() => navigate('/')}>
          Go to sign in
        </button>
      </Shell>
    );
  }

  // ---------------------------------------------------------------------------
  // Expired or Invalid — show resend form
  // ---------------------------------------------------------------------------
  if (pageState === STATE.EXPIRED || pageState === STATE.INVALID) {
    const isExpired = pageState === STATE.EXPIRED;
    return (
      <Shell>
        <h1 style={{ marginTop: '1rem' }}>{isExpired ? 'Link expired' : 'Invalid link'}</h1>
        <p className="subtitle">
          {isExpired
            ? 'This activation link is older than 48 hours.'
            : 'This activation link is not valid.'}
        </p>

        {resendStatus === 'sent' ? (
          <p style={{ marginTop: '1.5rem', color: 'var(--ink-muted)', fontSize: 'var(--font-base)' }}>
            A new activation link has been sent. Check your inbox.
          </p>
        ) : (
          <form onSubmit={handleResend} style={{ marginTop: '1.5rem' }}>
            <label className="field-label" htmlFor="resend-email">
              Enter your email to get a new link
            </label>
            <input
              id="resend-email"
              type="email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              placeholder="you@example.com"
              required
              style={{ marginBottom: '0.75rem' }}
            />
            {resendStatus === 'error' && (
              <p className="error-text" role="alert">Something went wrong. Please try again.</p>
            )}
            <button
              type="submit"
              className="primary-btn"
              style={{ width: '100%' }}
              disabled={resendStatus === 'sending'}
            >
              {resendStatus === 'sending' ? 'Sending…' : 'Send new activation link'}
            </button>
          </form>
        )}

        <button
          className="secondary-btn"
          style={{ width: '100%', marginTop: '0.75rem' }}
          onClick={() => navigate('/')}
        >
          Back to sign in
        </button>
      </Shell>
    );
  }

  // ---------------------------------------------------------------------------
  // Set password form
  // ---------------------------------------------------------------------------
  return (
    <Shell>
      <h1 style={{ marginTop: '1rem' }}>
        Welcome{userInfo?.firstName ? `, ${userInfo.firstName}` : ''}!
      </h1>
      <p className="subtitle">Set a password to activate your account.</p>

      <form className="auth-form" onSubmit={handleSetPassword} style={{ marginTop: '1.5rem' }}>
        <label className="field-label" htmlFor="new-password">Password</label>
        <input
          id="new-password"
          type="password"
          value={password}
          onChange={(e) => setPass(e.target.value)}
          placeholder="At least 8 characters"
          autoComplete="new-password"
          required
        />

        <label className="field-label" htmlFor="confirm-password">Confirm password</label>
        <input
          id="confirm-password"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Re-enter your password"
          autoComplete="new-password"
          required
        />

        {formError && <p className="error-text" role="alert">{formError}</p>}

        <button type="submit" className="primary-btn" disabled={submitting}>
          {submitting ? 'Activating…' : 'Activate account'}
        </button>
      </form>
    </Shell>
  );
}
